import React, { useEffect, useState } from 'react';
import { Button, Card, Form, ListGroup } from 'react-bootstrap';
import { CursosExternosService } from '../../../../service/cursosExternos.service';
import { GruposService } from '../../../../service/grupos.service';
import { Grupo } from '../../../../service/types/cursos.types';
import { AsignacionCursoExterno } from '../../../../service/types/cursosExternos.types';
import { SimpleUser } from '../../cursos/builder/AsignacionManager';

interface AsignacionCursoExternoManagerProps {
  cursoExternoId: string;
  fetchUsers: () => Promise<SimpleUser[]>;
}

export default function AsignacionCursoExternoManager({
  cursoExternoId,
  fetchUsers,
}: AsignacionCursoExternoManagerProps) {
  const [asignaciones, setAsignaciones] = useState<AsignacionCursoExterno[]>([]);
  const [users, setUsers] = useState<SimpleUser[]>([]);
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [modo, setModo] = useState<'usuario' | 'grupo'>('usuario');
  const [selectedRut, setSelectedRut] = useState('');
  const [selectedGrupoId, setSelectedGrupoId] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [asignacionesData, usersData, gruposData] = await Promise.all([
      CursosExternosService.listAsignaciones(cursoExternoId),
      fetchUsers(),
      GruposService.findAll(),
    ]);
    setAsignaciones(asignacionesData);
    setUsers(usersData);
    setGrupos(gruposData);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursoExternoId]);

  const handleAsignar = async () => {
    if (modo === 'usuario' && !selectedRut) {
      alert('Seleccione un usuario');
      return;
    }
    if (modo === 'grupo' && !selectedGrupoId) {
      alert('Seleccione un grupo');
      return;
    }
    setSaving(true);
    try {
      await CursosExternosService.asignar(
        cursoExternoId,
        modo === 'usuario' ? { usuarioRut: selectedRut } : { grupoId: selectedGrupoId },
      );
      setSelectedRut('');
      setSelectedGrupoId('');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (asignacionId: string) => {
    await CursosExternosService.removeAsignacion(asignacionId);
    await load();
  };

  return (
    <Card className="mb-3">
      <Card.Header>Asignaciones del curso externo</Card.Header>
      <Card.Body>
        <ListGroup className="mb-3">
          {asignaciones.map((asignacion) => (
            <ListGroup.Item key={asignacion.id} className="d-flex justify-content-between align-items-center">
              <span>
                {asignacion.grupo
                  ? `Grupo: ${asignacion.grupo.nombre}`
                  : `Usuario: ${asignacion.usuario?.name ?? asignacion.usuarioRut}`}
              </span>
              <Button size="sm" variant="outline-danger" onClick={() => handleRemove(asignacion.id)}>
                Quitar
              </Button>
            </ListGroup.Item>
          ))}
          {asignaciones.length === 0 && (
            <ListGroup.Item className="text-center text-muted">Este curso externo no tiene asignaciones</ListGroup.Item>
          )}
        </ListGroup>

        <Form.Group className="mb-2">
          <Form.Check
            inline
            type="radio"
            label="Asignar a usuario"
            name="modoAsignacionExterno"
            checked={modo === 'usuario'}
            onChange={() => setModo('usuario')}
          />
          <Form.Check
            inline
            type="radio"
            label="Asignar a grupo"
            name="modoAsignacionExterno"
            checked={modo === 'grupo'}
            onChange={() => setModo('grupo')}
          />
        </Form.Group>

        <div className="d-flex gap-2">
          {modo === 'usuario' ? (
            <Form.Select value={selectedRut} onChange={(e) => setSelectedRut(e.target.value)}>
              <option value="">Seleccione un usuario</option>
              {users.map((u) => (
                <option key={u.rut} value={u.rut}>
                  {u.name} ({u.rut})
                </option>
              ))}
            </Form.Select>
          ) : (
            <Form.Select value={selectedGrupoId} onChange={(e) => setSelectedGrupoId(e.target.value)}>
              <option value="">Seleccione un grupo</option>
              {grupos.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.nombre}
                </option>
              ))}
            </Form.Select>
          )}
          <Button variant="success" onClick={handleAsignar} disabled={saving}>
            Asignar
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
